import { Bell, LogOut, Menu, ShieldCheck } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "#/context/AuthProvider";
import { Button } from "#/components/common/Button";
import { getModuleDescription } from "#/components/app-shell/Sidebar";
import { fetchUnreadCount } from "#/features/dashboard/api";

type TopbarProps = {
  onOpenMobileNav: () => void;
};

/** Badge colour based on unread count thresholds */
function notifBadgeClass(count: number): string {
  if (count <= 4)  return "bg-blue-500";
  if (count <= 10) return "bg-emerald-500";
  return "bg-red-500";
}

/** Bell ring colour when count > 0 */
function bellClass(count: number): string {
  if (count <= 0)  return "text-slate-600 hover:text-emerald-700 hover:bg-emerald-50";
  if (count <= 4)  return "text-blue-600 hover:text-blue-700 hover:bg-blue-50";
  if (count <= 10) return "text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50";
  return "text-red-600 hover:text-red-700 hover:bg-red-50";
}

export function Topbar({ onOpenMobileNav }: TopbarProps) {
  const location = useLocation();
  const { signOut } = useAuth();

  const moduleInfo = getModuleDescription(location.pathname);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["notif-unread-count"],
    queryFn: fetchUnreadCount,
    staleTime: 1000 * 60,       // treat as fresh for 1 min
    refetchInterval: 1000 * 60, // poll every 60 s
  });

  const displayCount = unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">

        {/* Left — hamburger + page title */}
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onOpenMobileNav}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 lg:hidden"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="min-w-0">
            <h1 className="truncate text-sm font-bold text-slate-900 sm:text-base">
              {moduleInfo.title}
            </h1>
            {moduleInfo.description && (
              <p className="hidden truncate text-xs text-slate-500 sm:block">
                {moduleInfo.description}
              </p>
            )}
          </div>
        </div>

        {/* Right — actions */}
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">

          {/* ── Notification bell with coloured count badge ── */}
          <Link
            to="/notifications"
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
            title={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}` : "Notifications"}
            className={`relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm transition ${bellClass(unreadCount)}`}
          >
            <Bell className="h-5 w-5" />

            {displayCount && (
              <span
                className={`absolute -right-1.5 -top-1.5 flex min-w-[18px] items-center justify-center rounded-full px-1 py-px text-[10px] font-bold leading-none text-white shadow ${notifBadgeClass(unreadCount)}`}
              >
                {displayCount}
              </span>
            )}
          </Link>

          {/* ── Security settings ── */}
          <Link
            to="/account/security"
            className="group inline-flex h-10 items-center justify-center gap-2 overflow-hidden rounded-xl border border-slate-200 bg-white px-3 text-slate-600 shadow-sm transition hover:bg-emerald-50 hover:text-emerald-700"
            aria-label="Security settings"
            title="Security settings"
          >
            <ShieldCheck className="h-5 w-5 shrink-0" />
            <span className="max-w-0 whitespace-nowrap text-sm font-medium opacity-0 transition-all duration-200 group-hover:max-w-36 group-hover:opacity-100">
              Security
            </span>
          </Link>

          {/* ── Sign out ── */}
          <Button
            type="button"
            variant="secondary"
            onClick={() => void signOut()}
            className="inline-flex h-10 items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
